import { extensionHost } from '@/extensions/host';
import type { ExtensionState } from '@/extensions/types';
import { cn } from '@/lib/utils';
import { useExtensionStore } from '@/store/extensionStore';
import { AlertCircle, Package, Power, PowerOff, Puzzle, Trash2 } from 'lucide-react';

/* ================================================================== */
/*  Extension Panel Container                                         */
/*                                                                    */
/*  Renders extension-contributed sidebar panels when selected, and   */
/*  provides the Extensions Manager view for managing installed exts. */
/* ================================================================== */

/* -- State badge colors -------------------------------------------- */

const stateBadge: Record<ExtensionState, { color: string; label: string }> = {
  installed: { color: 'bg-gray-500', label: 'Installed' },
  activating: { color: 'bg-yellow-500 animate-pulse', label: 'Activating' },
  active: { color: 'bg-green-500', label: 'Active' },
  deactivating: { color: 'bg-yellow-500', label: 'Deactivating' },
  inactive: { color: 'bg-gray-500', label: 'Inactive' },
  disabled: { color: 'bg-gray-600', label: 'Disabled' },
  error: { color: 'bg-red-500', label: 'Error' },
};

/* ------------------------------------------------------------------ */
/*  Extension Card — single extension in the manager list             */
/* ------------------------------------------------------------------ */

interface ExtensionCardProps {
  id: string;
  name: string;
  version: string;
  description: string;
  author: string;
  state: ExtensionState;
  error?: string;
  categories?: string[];
}

function ExtensionCard({
  id,
  name,
  version,
  description,
  author,
  state,
  error,
  categories,
}: ExtensionCardProps) {
  const badge = stateBadge[state];
  const isActive = state === 'active';

  const handleToggle = async () => {
    if (isActive) {
      await extensionHost.deactivate(id);
    } else if (state === 'installed' || state === 'inactive') {
      await extensionHost.activate(id);
    }
  };

  const handleUninstall = async () => {
    await extensionHost.uninstall(id);
  };

  return (
    <div className="border border-[#3e3e3e] rounded-md p-3 mb-2 bg-[#2d2d2d] hover:bg-[#333] transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{name}</span>
            <span className="text-[10px] text-[#888] font-mono">v{version}</span>
          </div>
          <div className="text-[11px] text-[#888] mt-0.5">{author}</div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={cn('w-2 h-2 rounded-full shrink-0', badge.color)} title={badge.label} />
        </div>
      </div>

      {/* Description */}
      <p className="text-xs text-[#aaa] mt-2 line-clamp-2">{description}</p>

      {/* Categories */}
      {categories && categories.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {categories.map((cat) => (
            <span key={cat} className="text-[10px] px-1.5 py-0.5 bg-[#1e1e1e] text-[#888] rounded">
              {cat}
            </span>
          ))}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-start gap-1.5 mt-2 text-red-400 text-[11px]">
          <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
          <span className="break-all">{error}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={handleToggle}
          disabled={state === 'activating' || state === 'deactivating'}
          className={cn(
            'flex items-center gap-1 text-[11px] px-2 py-1 rounded transition-colors',
            isActive
              ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20'
              : 'bg-green-500/10 text-green-400 hover:bg-green-500/20',
            (state === 'activating' || state === 'deactivating') && 'opacity-50 cursor-not-allowed',
          )}
        >
          {isActive ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
          {isActive ? 'Deactivate' : 'Activate'}
        </button>

        <button
          onClick={handleUninstall}
          className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
        >
          <Trash2 className="w-3 h-3" />
          Uninstall
        </button>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Extensions Manager Panel                                          */
/* ================================================================== */

export function ExtensionsManagerPanel() {
  const extensions = useExtensionStore((s) => s.extensions);

  const extensionList = Array.from(extensions.values());
  const activeCount = extensionList.filter((e) => e.state === 'active').length;

  return (
    <div className="flex flex-col h-full">
      {/* Stats bar */}
      <div className="flex items-center gap-3 px-3 py-2 text-[11px] text-[#888] border-b border-[#2d2d2d]">
        <span>{extensionList.length} installed</span>
        <span className="text-green-400">{activeCount} active</span>
      </div>

      {/* Extension list */}
      <div className="flex-1 overflow-y-auto p-2">
        {extensionList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[#666] gap-3 py-8">
            <Puzzle className="w-10 h-10" />
            <div className="text-center">
              <p className="text-sm text-[#aaa]">No Extensions Installed</p>
              <p className="text-xs mt-1">
                Extensions add new panels, commands, deploy targets, and more to the platform.
              </p>
            </div>
          </div>
        ) : (
          extensionList.map((ext) => (
            <ExtensionCard
              key={ext.manifest.id}
              id={ext.manifest.id}
              name={ext.manifest.name}
              version={ext.manifest.version}
              description={ext.manifest.description}
              author={ext.manifest.author}
              state={ext.state}
              error={ext.error}
              categories={ext.manifest.categories}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-[#2d2d2d] text-[10px] text-[#666]">
        KuberNodes Extension Platform v1.0
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Extension Panel Renderer                                          */
/*                                                                    */
/*  Given a panel id contributed by an extension, renders it with     */
/*  the correct props.                                                */
/* ================================================================== */

interface ExtensionPanelRendererProps {
  panelId: string;
  workspaceId: string;
}

export function ExtensionPanelRenderer({ panelId, workspaceId }: ExtensionPanelRendererProps) {
  const panels = useExtensionStore((s) => s.panels);
  const extensions = useExtensionStore((s) => s.extensions);

  const panel = panels.find((p) => `ext:${p.extensionId}:${p.id}` === panelId);
  if (!panel) {
    return (
      <div className="flex items-center justify-center h-full text-[#666] text-sm">
        Panel not found
      </div>
    );
  }

  const ext = extensions.get(panel.extensionId);
  if (!ext || ext.state !== 'active') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-[#666] text-sm gap-2">
        <Package className="w-6 h-6" />
        <span>Extension is not active</span>
      </div>
    );
  }

  const PanelComponent = panel.component;
  // Build a minimal API — panels get the same API the extension received
  const api = ext.api ?? ({} as import('@/extensions/types').ExtensionAPI);

  return <PanelComponent api={api} workspaceId={workspaceId} />;
}

export default ExtensionsManagerPanel;
